/**
 * QuestionnaireForm
 * Beautiful questionnaire UI following brand guidelines
 */

import { useState, useRef, useEffect } from 'react';
import { Question } from '@/types';
import { CustomSelect } from './CustomSelect';
import './styles/QuestionnaireForm.css';

type QuestionnaireFormProps = {
  questions: Question[];
  onSubmit: (
    responses: Record<string, string | number | boolean | string[]>
  ) => void;
  onBack?: () => void;
};

export function QuestionnaireForm({
  questions,
  onSubmit,
  onBack,
}: QuestionnaireFormProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [responses, setResponses] = useState<
    Record<string, string | number | boolean | string[]>
  >({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const validationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, []);

  // Separate email question from other questions
  const emailQuestion = questions.find((q) => q.id === 'email');
  const regularQuestions = questions.filter((q) => q.id !== 'email');

  // Total steps = regular questions + email step
  const totalSteps = regularQuestions.length + 1;
  const isEmailStep = currentSlide === regularQuestions.length;
  const currentQuestion = isEmailStep
    ? emailQuestion
    : regularQuestions[currentSlide];
  const isLastQuestion = currentSlide === totalSteps - 1;
  const progress = ((currentSlide + 1) / totalSteps) * 100;

  const validateCurrentQuestion = (): boolean => {
    if (!currentQuestion) return true;
    if (!currentQuestion.required) return true;

    const value = responses[currentQuestion.id];

    if (value === undefined || value === null || value === '') {
      setErrors({ [currentQuestion.id]: 'This field is required' });
      return false;
    }

    if (
      currentQuestion.type === 'multi-select' &&
      Array.isArray(value) &&
      value.length === 0
    ) {
      setErrors({ [currentQuestion.id]: 'Please select at least one option' });
      return false;
    }

    if (currentQuestion.type === 'email' && typeof value === 'string') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        setErrors({
          [currentQuestion.id]: 'Please enter a valid email address',
        });
        return false;
      }
    }

    setErrors({});
    return true;
  };

  const handleNext = () => {
    if (!validateCurrentQuestion()) return;

    if (isLastQuestion) {
      onSubmit(responses);
    } else {
      setCurrentSlide((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentSlide > 0) {
      setCurrentSlide((prev) => prev - 1);
      setErrors({});
    } else if (onBack) {
      onBack();
    }
  };

  const handleChange = (value: string | number | boolean | string[]) => {
    if (!currentQuestion) return;
    setResponses((prev) => ({
      ...prev,
      [currentQuestion.id]: value,
    }));

    // Clear errors immediately on change
    setErrors({});

    // Clear any pending validation timeout
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    // Debounced email validation (1 second delay)
    if (
      currentQuestion.type === 'email' &&
      typeof value === 'string' &&
      value.length > 0
    ) {
      validationTimeoutRef.current = setTimeout(() => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          setErrors({
            [currentQuestion.id]: 'Please enter a valid email address',
          });
        }
      }, 2000);
    }
  };

  const renderInput = () => {
    if (!currentQuestion) return null;
    const value = responses[currentQuestion.id];

    switch (currentQuestion.type) {
      case 'text':
      case 'email':
        return (
          <input
            type={currentQuestion.type}
            value={(value as string) || ''}
            onChange={(e) => handleChange(e.target.value)}
            className={`question-input ${currentQuestion.id && errors[currentQuestion.id] ? 'error' : ''}`}
            placeholder={
              currentQuestion.type === 'email'
                ? 'your@email.com'
                : 'Type your answer here...'
            }
            autoFocus
          />
        );

      case 'textarea':
        return (
          <textarea
            value={(value as string) || ''}
            onChange={(e) => handleChange(e.target.value)}
            className="question-textarea"
            placeholder="Tell us more..."
            rows={4}
            autoFocus
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={(value as number) || ''}
            onChange={(e) => handleChange(Number(e.target.value))}
            className="question-input"
            placeholder="Enter a number..."
            autoFocus
          />
        );

      case 'select':
        return (
          <div className="question-select-wrapper">
            <CustomSelect
              value={(value as string) || ''}
              onChange={(val) => handleChange(val)}
              options={[
                { value: '', label: 'Choose an option...' },
                ...(currentQuestion.options?.map((option) => ({
                  value: option,
                  label: option,
                })) || []),
              ]}
              placeholder="Choose an option..."
              autoFocus
            />
          </div>
        );

      case 'radio':
        return (
          <div className="question-options">
            {currentQuestion.options?.map((option) => (
              <label key={option} className="question-option-label">
                <input
                  type="radio"
                  name={currentQuestion.id}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleChange(e.target.value)}
                  className="question-radio"
                />
                <span className="question-option-text">{option}</span>
              </label>
            ))}
          </div>
        );

      case 'multi-select': {
        const selectedValues = (value as string[]) || [];
        return (
          <div className="question-options">
            {currentQuestion.options?.map((option) => (
              <label key={option} className="question-option-label">
                <input
                  type="checkbox"
                  value={option}
                  checked={selectedValues.includes(option)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...selectedValues, option]
                      : selectedValues.filter((v) => v !== option);
                    handleChange(newValues);
                  }}
                  className="question-checkbox"
                />
                <span className="question-option-text">{option}</span>
              </label>
            ))}
          </div>
        );
      }

      case 'boolean':
        return (
          <div className="question-options question-boolean">
            <button
              type="button"
              className={`question-boolean-btn ${value === true ? 'active' : ''}`}
              onClick={() => handleChange(true)}
            >
              Yes
            </button>
            <button
              type="button"
              className={`question-boolean-btn ${value === false ? 'active' : ''}`}
              onClick={() => handleChange(false)}
            >
              No
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="questionnaire-form">
      <div className="questionnaire-header-photobooth">
        <h2 className="questionnaire-title-photobooth">
          {isEmailStep
            ? 'Where can we send your photos?'
            : 'Last step before we send you your photos!'}
        </h2>
        <div className="questionnaire-progress">
          <div
            className="questionnaire-progress-bar"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="questionnaire-step">
          {isEmailStep
            ? `Final Step`
            : `Question ${currentSlide + 1} of ${regularQuestions.length}`}
        </p>
      </div>

      <div className="questionnaire-body">
        <div className="question-slide">
          <label className="question-label">
            {currentQuestion?.label}
            {currentQuestion?.required && (
              <span className="question-required">*</span>
            )}
          </label>

          {renderInput()}

          {currentQuestion?.id && errors[currentQuestion.id] && (
            <p className="question-error">{errors[currentQuestion.id]}</p>
          )}
        </div>
      </div>

      <div className="questionnaire-actions">
        <button
          onClick={handleBack}
          className="btn-questionnaire-back"
          type="button"
        >
          {currentSlide === 0 ? 'Back to Photos' : 'Previous'}
        </button>
        <button
          onClick={handleNext}
          className="btn-questionnaire-next"
          type="button"
        >
          {isLastQuestion ? 'All Done! 🎉' : 'Next'}
        </button>
      </div>
    </div>
  );
}
